// src/components/profile/FollowModal.tsx

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  fetchFollowers,
  fetchFollowing,
  followUser,
  unfollowUser,
} from "../../services/followService";
import { FollowUserModel } from "../../models/FollowModel";
import { useAuth } from "../../context/AuthContext";
import { SpinnerLoading } from "../../utils";
import "./FollowModal.css";

export type FollowModalTab = "followers" | "following";

interface FollowModalProps {
  profileUserId: number;
  profileUsername: string;
  initialTab: FollowModalTab;
  onClose: () => void;
}

const PAGE_SIZE = 20;

/**
 * Modal overlay that shows a paginated follower or following list.
 * Renders inline follow/unfollow toggles for each row (for authenticated users).
 */
const FollowModal: React.FC<FollowModalProps> = ({
  profileUserId,
  profileUsername,
  initialTab,
  onClose,
}) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();

  const [activeTab, setActiveTab] = useState<FollowModalTab>(initialTab);
  const [users, setUsers] = useState<FollowUserModel[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  // Tracks in-flight toggle requests by userId to prevent double-clicking
  const [toggling, setToggling] = useState<Set<number>>(new Set());

  const loadPage = useCallback(
    async (tab: FollowModalTab, pg: number) => {
      setLoading(true);
      try {
        const res =
          tab === "followers"
            ? await fetchFollowers(profileUserId, pg, PAGE_SIZE)
            : await fetchFollowing(profileUserId, pg, PAGE_SIZE);

        // Append on next page, replace on first page
        setUsers((prev) => (pg === 0 ? res.content : [...prev, ...res.content]));
        setTotalPages(res.totalPages);
        setPage(pg);
      } catch {
        // Silently ignore — empty list is acceptable
      } finally {
        setLoading(false);
      }
    },
    [profileUserId]
  );

  // Reload when tab changes
  useEffect(() => {
    setUsers([]);
    setPage(0);
    loadPage(activeTab, 0);
  }, [activeTab, loadPage]);

  const handleTabChange = (tab: FollowModalTab) => {
    if (tab !== activeTab) setActiveTab(tab);
  };

  const handleToggleFollow = async (targetUser: FollowUserModel) => {
    if (!currentUser || toggling.has(targetUser.userId)) return;

    setToggling((prev) => new Set(prev).add(targetUser.userId));

    const wasFollowing = targetUser.isFollowing;
    // Optimistic update
    setUsers((prev) =>
      prev.map((u) =>
        u.userId === targetUser.userId ? { ...u, isFollowing: !wasFollowing } : u
      )
    );

    try {
      if (wasFollowing) {
        await unfollowUser(targetUser.userId);
      } else {
        await followUser(targetUser.userId);
      }
    } catch {
      // Roll back
      setUsers((prev) =>
        prev.map((u) =>
          u.userId === targetUser.userId ? { ...u, isFollowing: wasFollowing } : u
        )
      );
    } finally {
      setToggling((prev) => {
        const next = new Set(prev);
        next.delete(targetUser.userId);
        return next;
      });
    }
  };

  const buildProfileSlug = (username: string, userId: number) =>
    `${username}--${userId}`;

  return (
    <div className="follow-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="follow-modal">
        {/* Header */}
        <div className="follow-modal__header">
          <span className="follow-modal__title">{profileUsername}</span>
          <button className="follow-modal__close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="follow-modal__tabs">
          <button
            className={`follow-modal__tab ${activeTab === "followers" ? "follow-modal__tab--active" : ""}`}
            onClick={() => handleTabChange("followers")}
          >
            {t("follow_followers")}
          </button>
          <button
            className={`follow-modal__tab ${activeTab === "following" ? "follow-modal__tab--active" : ""}`}
            onClick={() => handleTabChange("following")}
          >
            {t("follow_following")}
          </button>
        </div>

        {/* List */}
        <div className="follow-modal__list">
          {users.length === 0 && !loading && (
            <div className="follow-modal__empty">
              {activeTab === "followers"
                ? t("follow_no_followers")
                : t("follow_no_following")}
            </div>
          )}

          {users.map((u) => (
            <div key={u.userId} className="follow-modal__row">
              <Link
                to={`/yazar/${buildProfileSlug(u.username, u.userId)}`}
                className="follow-modal__username"
                onClick={onClose}
              >
                {u.username}
              </Link>

              {/* Show follow toggle only to authenticated users who are not viewing themselves */}
              {currentUser && currentUser.id !== u.userId && (
                <button
                  className={`follow-modal__toggle ${u.isFollowing ? "follow-modal__toggle--following" : ""}`}
                  onClick={() => handleToggleFollow(u)}
                  disabled={toggling.has(u.userId)}
                >
                  {u.isFollowing ? t("follow_unfollow") : t("follow_follow")}
                </button>
              )}
            </div>
          ))}

          {loading && <SpinnerLoading />}

          {/* Load more */}
          {!loading && page + 1 < totalPages && (
            <button
              className="follow-modal__load-more"
              onClick={() => loadPage(activeTab, page + 1)}
            >
              {t("follow_load_more")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowModal;